import moment from 'moment';
import { Verb, trackUe, Noun } from '@trello/analytics';
import { hasAdvancedChecklistsBeta } from './has-advanced-checklists';

interface AdvancedCheckItem {
  name: string;
  validAssigneeUsernames: string[];
  orgProductNumber?: number;
}

export function getAssigneeUsername(
  checkItem: AdvancedCheckItem,
): string | null {
  if (!hasAdvancedChecklistsBeta(checkItem.orgProductNumber)) {
    return null;
  }

  const initialMention = checkItem.name.match(/^@([a-z0-9_]{3,})\b/)?.[1];

  const assigneeUsername =
    initialMention && checkItem.validAssigneeUsernames.includes(initialMention)
      ? initialMention
      : null;

  return assigneeUsername;
}

export function getDueDate(checkItem: AdvancedCheckItem) {
  if (!hasAdvancedChecklistsBeta(checkItem.orgProductNumber)) {
    return null;
  }

  const potentialDueDate = checkItem.name.match(/\b\d\d\d\d-\d\d-\d\d$/)?.[0];

  const momentDate = potentialDueDate && moment(potentialDueDate);

  const due = momentDate && momentDate.isValid() ? momentDate.toDate() : null;

  return due;
}

export function trackNameChange(
  checkItem: AdvancedCheckItem,
  prevName: string,
  trackingMethod: string,
) {
  const prevAssigneeUsername = getAssigneeUsername({
    ...checkItem,
    name: prevName,
  });
  const prevDueDate = getDueDate({
    ...checkItem,
    name: prevName,
  });

  const newAssigneeUsername = getAssigneeUsername(checkItem);
  const newDueDate = getDueDate(checkItem);

  if (
    (prevAssigneeUsername || newAssigneeUsername) &&
    prevAssigneeUsername !== newAssigneeUsername
  ) {
    let verb = Verb.CHANGES;
    if (!prevAssigneeUsername && newAssigneeUsername) {
      verb = Verb.ADDS;
    } else if (prevAssigneeUsername && !newAssigneeUsername) {
      verb = Verb.REMOVES;
    }
    trackUe(
      Noun.CARD_DETAIL,
      verb,
      Noun.MEMBER,
      'on checkitem',
      '',
      trackingMethod,
    );
  }

  if (
    (prevDueDate || newDueDate) &&
    prevDueDate?.getTime() !== newDueDate?.getTime()
  ) {
    let verb = Verb.CHANGES;
    if (!prevDueDate && newDueDate) {
      verb = Verb.ADDS;
    } else if (prevDueDate && !newDueDate) {
      verb = Verb.REMOVES;
    }
    trackUe(
      Noun.CARD_DETAIL,
      verb,
      Noun.DUE_DATE,
      'on checkitem',
      '',
      trackingMethod,
    );
  }
}

export function trackNameRender(checkItem: AdvancedCheckItem) {
  const prepObj = hasAdvancedChecklistsBeta(checkItem.orgProductNumber)
    ? 'on advanced checklists'
    : '';

  trackUe(
    Noun.CARD_DETAIL,
    Verb.RENDERS,
    Noun.CHECKLIST_ITEM,
    prepObj,
    '',
    'by viewing card detail',
  );

  if (getAssigneeUsername(checkItem)) {
    trackUe(
      Noun.CARD_DETAIL,
      Verb.RENDERS,
      Noun.MEMBER,
      'on checkitem',
      '',
      'by having an mention at the beginning of checkitem text',
    );
  }

  if (getDueDate(checkItem)) {
    trackUe(
      Noun.CARD_DETAIL,
      Verb.RENDERS,
      Noun.DUE_DATE,
      'on checkitem',
      '',
      'by having a date-like string at the end of checkitem text',
    );
  }
}
